import React, { useCallback, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  RadioGroup, RadioGroupItem,
} from './ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Slider } from './ui/slider';
import {
  CheckCircle2, ChevronLeft, ChevronRight, Loader2, Sparkles,
  Target, Eye, Wand2, Layers, Lightbulb, Compass, Trophy, Heart,
} from 'lucide-react';
import { toast } from 'sonner';
import i18n from '../i18n';

const zt = (k, d, o) => i18n.t(`zoom.${k}`, d, o);
import { api } from '../lib/api';
import { logger } from '../lib/logger';

/**
 * ZoomReportFormModal — 8-section structured report based on the
 * "HCF Senior Zoom System · 30 minuti" playbook.
 *
 * Triggered from the LeadDetailPage when the Senior clicks
 * "Ha risposto + Compila report Zoom". On submit:
 *   1. POST /api/lifecycle/:lead_id/zoom-report  (with structured payload)
 *   2. Backend sends rich HTML email to morena@housecashflows.com
 *   3. Backend DMs the ORIGINAL Associate (Form-1 owner) on Telegram
 *   4. Lead stage is updated (call_done_positive | call_done_neutral | call_no_show)
 */

const PROFILE_OPTIONS = [
  { value: 'LIFE',   label: 'LIFE · Lifestyle buyer' },
  { value: 'TIME',   label: 'TIME · Opportunista strategico' },
  { value: 'SAFE',   label: 'SAFE · Protettivo patrimoniale' },
  { value: 'CASH',   label: 'CASH · Cashflow buyer' },
  { value: 'FAME',   label: 'FAME · Status silenzioso' },
  { value: 'VISION', label: 'VISION · Visionario' },
  { value: 'DEEP',   label: 'DEEP · Analitico' },
  { value: 'HOME',   label: 'HOME · Family buyer' },
];

const PATH_OPTIONS = [
  { value: 'equity_share', label: 'Equity Share' },
  { value: 'real_estate', get label() { return zt('path.realEstate', 'Real Estate (proprietà piena)'); } },
  { value: 'both', get label() { return zt('path.both', 'Entrambi (Equity + Real Estate)'); } },
];

const RESORT_OPTIONS = [
  { value: 'lombok',   label: '🇮🇩 Lombok (Indonesia)' },
  { value: 'spille',   label: '🇦🇱 Spille (Albania)' },
  { value: 'phuket',   label: '🇹🇭 Phuket (Thailandia)' },
  { value: 'multi',    label: '🌍 Multi-resort' },
];

const REACTION_OPTIONS = [
  { value: 'entusiasta', get label() { return `🤩 ${zt('react.enthusiast', 'Entusiasta')}`; } },
  { value: 'interessato', get label() { return `👍 ${zt('react.interested', 'Interessato')}`; } },
  { value: 'tiepido', get label() { return `😐 ${zt('react.lukewarm', 'Tiepido')}`; } },
  { value: 'scettico', get label() { return `🤨 ${zt('react.skeptical', 'Scettico')}`; } },
];

const DIFFERENTIATORS = [
  'governance_HCF', 'asset_reale', 'community_soci', 'brand_internazionale',
  'tax_engine', 'mercato_secondario', 'tre_destinazioni',
];
const DIFF_LABELS = {
  governance_HCF:        'Governance HCF',
  get asset_reale() { return zt('diff.realAsset', 'Asset reale (resort)'); },
  get community_soci() { return zt('diff.community', 'Community soci'); },
  get brand_internazionale() { return zt('diff.brand', 'Brand internazionale'); },
  get tax_engine() { return zt('diff.tax', 'Tax engine multi-paese'); },
  get mercato_secondario() { return zt('diff.secondary', 'Mercato secondario interno'); },
  get tre_destinazioni() { return zt('diff.threeDest', '3 destinazioni · 1 quota'); },
};

const OUTCOME_OPTIONS = [
  { value: 'procede', get label() { return `🟢 ${zt('out.proceeds', 'PROCEDE')}`; }, get desc() { return zt('out.proceedsDesc', 'Ha pagato o firmato il primo step'); } },
  { value: 'interessato', get label() { return `🟡 ${zt('out.interested', 'INTERESSATO')}`; }, get desc() { return zt('out.interestedDesc', 'Vuole follow-up programmato'); } },
  { value: 'riflessione', get label() { return `🟠 ${zt('out.thinking', 'RIFLESSIONE')}`; }, get desc() { return zt('out.thinkingDesc', 'Non ora, da ricontattare'); } },
  { value: 'non_interessato', get label() { return `🔴 ${zt('out.notInterested', 'NON INTERESSATO')}`; }, get desc() { return zt('out.notInterestedDesc', 'Chiuso'); } },
  { value: 'no_show', label: '⚫ NO-SHOW', get desc() { return zt('out.noShowDesc', 'Non si è presentato'); } },
];

const SECTIONS = [
  { idx: 1, time: '0-3 min', icon: Heart, get title() { return zt('sec1', 'Entrata e continuità'); } },
  { idx: 2, time: '3-7 min', icon: Target, get title() { return zt('sec2', 'Conferma profilo cliente'); } },
  { idx: 3, time: '7-11 min', icon: Eye, get title() { return zt('sec3', 'Lettura strategica · specchio'); } },
  { idx: 4, time: '11-16 min', icon: Wand2, get title() { return zt('sec4', 'Costruzione visione'); } },
  { idx: 5, time: '16-22 min', icon: Lightbulb, get title() { return zt('sec5', 'Solution + convenience'); } },
  { idx: 6, time: '22-24 min', icon: Layers, get title() { return zt('sec6', 'Experience HCF'); } },
  { idx: 7, time: '24-28 min', icon: Compass, get title() { return zt('sec7', 'Proposta reale'); } },
  { idx: 8, time: '28-30 min', icon: Trophy, get title() { return zt('sec8', 'Chiusura e pagamento'); } },
];

export default function ZoomReportFormModal({
  open,
  onOpenChange,
  lead,
  onSubmitted,
}) {
  const [step, setStep] = useState(0); // 0..7 (sections 1..8)
  const [submitting, setSubmitting] = useState(false);
  const [attended, setAttended] = useState(true);
  const [structured, setStructured] = useState(() => ({
    // Section 1
    trust_built: '',
    associate_notes_confirmed: '',
    section1_notes: '',
    // Section 2
    profile_confirmed: '',
    profile_secondary: '',
    client_needs: '',
    client_goals: '',
    // Section 3
    alignment_score: 7,
    mirror_phrase: '',
    // Section 4
    vision_pitched: '',
    client_reaction: '',
    // Section 5
    solution_proposed: '',
    objections: '',
    // Section 6
    hcf_differentiators: [],
    hcf_client_response: '',
    // Section 7
    path_chosen: '',
    resort_chosen: '',
    units_count: '',
    amount_proposed: '',
    concrete_interest: '',
    // Section 8
    outcome: '',
    amount_confirmed: '',
    next_step_date: '',
    next_step_desc: '',
    // Final
    lead_temperature_final: 6,
    notes_final: '',
  }));

  const setField = useCallback((k, v) => setStructured((p) => ({ ...p, [k]: v })), []);

  const toggleDiff = useCallback((d) => setStructured((p) => ({
    ...p,
    hcf_differentiators: p.hcf_differentiators.includes(d)
      ? p.hcf_differentiators.filter((x) => x !== d)
      : [...p.hcf_differentiators, d],
  })), []);

  // No-show path: when the lead didn't attend, we skip the entire form and
  // just send a minimal report (attended=false + outcome=no_show).
  const handleNoShowSubmit = useCallback(async () => {
    if (!lead?.id) return;
    setSubmitting(true);
    try {
      await api.post(`/lifecycle/${lead.id}/zoom-report`, {
        attended: false,
        transcript: '',
        proposals: '',
        structured: { outcome: 'no_show' },
      });
      toast.success('No-show registrato · Morena e Associate notificati');
      onSubmitted?.();
      onOpenChange(false);
    } catch (err) {
      logger.error('zoom-report no-show submit failed', err);
      toast.error(zt('sendErrorRetry', 'Errore invio report. Riprova.'));
    } finally {
      setSubmitting(false);
    }
  }, [lead?.id, onOpenChange, onSubmitted]);

  const handleSubmit = useCallback(async () => {
    if (!lead?.id) return;
    if (!structured.outcome) {
      toast.error(zt('outcomeRequired', 'Seleziona un esito nella sezione 8 prima di inviare.'));
      setStep(7);
      return;
    }
    setSubmitting(true);
    try {
      // Coerce numeric fields
      const payload = {
        attended: true,
        transcript: '',
        proposals: structured.solution_proposed || structured.notes_final || '',
        structured: {
          ...structured,
          alignment_score: Number(structured.alignment_score) || null,
          lead_temperature_final: Number(structured.lead_temperature_final) || null,
          units_count: structured.units_count ? Number(structured.units_count) : null,
          amount_proposed: structured.amount_proposed ? Number(structured.amount_proposed) : null,
          amount_confirmed: structured.amount_confirmed ? Number(structured.amount_confirmed) : null,
        },
      };
      await api.post(`/lifecycle/${lead.id}/zoom-report`, payload);
      toast.success(zt('sent', 'Report inviato · Morena + Associate notificati'));
      onSubmitted?.();
      onOpenChange(false);
    } catch (err) {
      logger.error('zoom-report submit failed', err);
      const detail = err?.response?.data?.detail || zt('sendError', 'Errore invio report');
      toast.error(typeof detail === 'string' ? detail : zt('sendError', 'Errore invio report'));
    } finally {
      setSubmitting(false);
    }
  }, [lead?.id, structured, onOpenChange, onSubmitted]);

  const progressPct = useMemo(() => Math.round(((step + 1) / SECTIONS.length) * 100), [step]);
  const SectionIcon = SECTIONS[step].icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="zoom-report-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[var(--gp-gold,#C9A34A)]" />
            {zt('title', 'Report Zoom Strategica · 30 min')}
          </DialogTitle>
          <DialogDescription>
            Lead: <b>{lead?.name || '—'}</b> · <span className="text-xs">{lead?.email || ''}</span>
          </DialogDescription>
        </DialogHeader>

        {/* No-show toggle */}
        {!attended && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 my-2">
            <p className="text-sm text-amber-900">
              {zt('noShowBanner', 'Stai registrando una no-show. Il lead non si è presentato alla Zoom.')}
            </p>
            <div className="flex gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={() => setAttended(true)}>{zt('cancelWasPresent', 'Annulla, era presente')}</Button>
              <Button size="sm" onClick={handleNoShowSubmit} disabled={submitting} data-testid="zoom-noshow-submit">
                {submitting ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> {zt('sending', 'Invio…')}</> : zt('confirmNoShow', 'Conferma no-show')}
              </Button>
            </div>
          </div>
        )}

        {attended && (
          <>
            {/* Quick "no-show" entry point */}
            <div className="flex items-center justify-between text-xs text-muted-foreground py-1">
              <span>
                {zt('section', 'Sezione')} <b>{SECTIONS[step].idx}</b>/8 · <span className="text-[var(--gp-gold,#C9A34A)]">{SECTIONS[step].time}</span>
              </span>
              <button
                type="button"
                className="underline text-xs hover:text-foreground"
                onClick={() => setAttended(false)}
                data-testid="zoom-mark-noshow"
              >
                Il lead non si è presentato →
              </button>
            </div>

            {/* Progress bar */}
            <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-[var(--gp-forest,#0F2A1B)] transition-all duration-300"
                style={{ width: `${progressPct}%` }}
              />
            </div>

            {/* Section card */}
            <div className="bg-zinc-50/60 border border-zinc-200/60 rounded-lg p-4 mt-3" data-testid={`zoom-section-${SECTIONS[step].idx}`}>
              <div className="flex items-center gap-2 mb-3">
                <SectionIcon className="w-4 h-4 text-[var(--gp-forest,#0F2A1B)]" />
                <h3 className="font-serif text-lg text-[var(--gp-forest,#0F2A1B)]">{SECTIONS[step].title}</h3>
              </div>

              {step === 0 && <Section1 v={structured} set={setField} />}
              {step === 1 && <Section2 v={structured} set={setField} />}
              {step === 2 && <Section3 v={structured} set={setField} />}
              {step === 3 && <Section4 v={structured} set={setField} />}
              {step === 4 && <Section5 v={structured} set={setField} />}
              {step === 5 && <Section6 v={structured} set={setField} toggleDiff={toggleDiff} />}
              {step === 6 && <Section7 v={structured} set={setField} />}
              {step === 7 && <Section8 v={structured} set={setField} />}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-4 gap-2">
              <Button
                variant="ghost"
                disabled={step === 0 || submitting}
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                data-testid="zoom-prev"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> {i18n.t('common.back', 'Indietro')}
              </Button>
              {step < SECTIONS.length - 1 ? (
                <Button
                  onClick={() => setStep((s) => Math.min(SECTIONS.length - 1, s + 1))}
                  data-testid="zoom-next"
                  className="bg-[var(--gp-forest,#0F2A1B)] hover:bg-[var(--gp-forest-dark,#082019)] text-white"
                >
                  {zt('next', 'Avanti')} <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || !structured.outcome}
                  data-testid="zoom-submit"
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Invio…</>
                  ) : (
                    <><CheckCircle2 className="w-4 h-4 mr-1.5" /> {zt('sendReport', 'Invia report')}</>
                  )}
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Section subcomponents (kept inline for cohesion with the playbook) ──

function YesNo({ value, onChange, testid }) {
  return (
    <RadioGroup value={value} onValueChange={onChange} className="flex gap-4 mt-1" data-testid={testid}>
      <label className="flex items-center gap-1.5 text-sm cursor-pointer">
        <RadioGroupItem value="si" id={`${testid}-si`} /> {zt('yes', 'Sì')}
      </label>
      <label className="flex items-center gap-1.5 text-sm cursor-pointer">
        <RadioGroupItem value="parziale" id={`${testid}-p`} /> Parziale
      </label>
      <label className="flex items-center gap-1.5 text-sm cursor-pointer">
        <RadioGroupItem value="no" id={`${testid}-no`} /> No
      </label>
    </RadioGroup>
  );
}

function Section1({ v, set }) {
  return (
    <div className="space-y-3">
      <div>
        <Label>{zt('s1trust', 'Hai instaurato fiducia nei primi 3 minuti?')}</Label>
        <YesNo value={v.trust_built} onChange={(x) => set('trust_built', x)} testid="zoom-trust" />
      </div>
      <div>
        <Label>{zt('s1notesConfirmed', 'Le note dell\'Associate sul lead sono state confermate?')}</Label>
        <YesNo value={v.associate_notes_confirmed} onChange={(x) => set('associate_notes_confirmed', x)} testid="zoom-notes-conf" />
      </div>
      <div>
        <Label htmlFor="z-s1notes">{zt('s1freeNotes', 'Note libere sull\'entrata')}</Label>
        <Textarea id="z-s1notes" rows={3} value={v.section1_notes} onChange={(e) => set('section1_notes', e.target.value)} placeholder={zt('s1notesPh', "Es. il lead ha risposto sereno, ha ricordato il nome dell'Associate…")} />
      </div>
    </div>
  );
}

function Section2({ v, set }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label>{zt('dominantConfirmed', 'Profilo dominante confermato')}</Label>
          <Select value={v.profile_confirmed} onValueChange={(x) => set('profile_confirmed', x)}>
            <SelectTrigger data-testid="zoom-profile-primary"><SelectValue placeholder={i18n.t('stageTransition.selectPh', 'Seleziona…')} /></SelectTrigger>
            <SelectContent>
              {PROFILE_OPTIONS.map((p) => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{i18n.t('manualProfile.secondary', 'Profilo secondario')}</Label>
          <Select value={v.profile_secondary} onValueChange={(x) => set('profile_secondary', x)}>
            <SelectTrigger><SelectValue placeholder={i18n.t('stageTransition.selectPh', 'Seleziona…')} /></SelectTrigger>
            <SelectContent>
              {PROFILE_OPTIONS.map((p) => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label htmlFor="z-needs">{zt('needs', 'Bisogni emersi durante la conversazione')}</Label>
        <Textarea id="z-needs" rows={3} value={v.client_needs} onChange={(e) => set('client_needs', e.target.value)} placeholder={zt('needsPh', 'Es. vuole proteggere il capitale dalla svalutazione…')} />
      </div>
      <div>
        <Label htmlFor="z-goals">{zt('goals12m', 'Obiettivi a 12 mesi che ha condiviso')}</Label>
        <Textarea id="z-goals" rows={3} value={v.client_goals} onChange={(e) => set('client_goals', e.target.value)} />
      </div>
    </div>
  );
}

function Section3({ v, set }) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="flex items-center justify-between">
          <span>Allineamento percepito (1-10)</span>
          <Badge variant="outline">{v.alignment_score}/10</Badge>
        </Label>
        <Slider value={[v.alignment_score]} onValueChange={(x) => set('alignment_score', x[0])} min={1} max={10} step={1} className="mt-2" />
      </div>
      <div>
        <Label htmlFor="z-mirror">{zt('mirror', 'Frase-specchio usata in chiamata')}</Label>
        <Textarea id="z-mirror" rows={3} value={v.mirror_phrase} onChange={(e) => set('mirror_phrase', e.target.value)} placeholder="Es. «Quello che mi dici mi fa pensare che cerchi non solo un investimento, ma un punto fermo»" />
      </div>
    </div>
  );
}

function Section4({ v, set }) {
  return (
    <div className="space-y-3">
      <div>
        <Label htmlFor="z-vision">{zt('vision', 'Visione presentata (cosa hai dipinto al cliente)')}</Label>
        <Textarea id="z-vision" rows={4} value={v.vision_pitched} onChange={(e) => set('vision_pitched', e.target.value)} placeholder="Es. la vita da socio Green Paradise · 3 case · community internazionale" />
      </div>
      <div>
        <Label>{zt('reaction', 'Reazione del cliente')}</Label>
        <Select value={v.client_reaction} onValueChange={(x) => set('client_reaction', x)}>
          <SelectTrigger data-testid="zoom-reaction"><SelectValue placeholder={i18n.t('stageTransition.selectPh', 'Seleziona…')} /></SelectTrigger>
          <SelectContent>
            {REACTION_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function Section5({ v, set }) {
  return (
    <div className="space-y-3">
      <div>
        <Label htmlFor="z-solution">{zt('solution', 'Soluzione proposta (com\'è stato strutturato il pitch)')}</Label>
        <Textarea id="z-solution" rows={4} value={v.solution_proposed} onChange={(e) => set('solution_proposed', e.target.value)} />
      </div>
      <div>
        <Label htmlFor="z-objections">{zt('objections', 'Obiezioni emerse')}</Label>
        <Textarea id="z-objections" rows={3} value={v.objections} onChange={(e) => set('objections', e.target.value)} placeholder="Es. «Il prezzo è alto», «Devo parlarne col commercialista»…" />
      </div>
    </div>
  );
}

function Section6({ v, toggleDiff, set }) {
  return (
    <div className="space-y-3">
      <Label>{zt('diffPoints', 'Punti di differenziazione mostrati')}</Label>
      <div className="grid grid-cols-2 gap-2">
        {DIFFERENTIATORS.map((d) => (
          <label key={d} className="flex items-center gap-2 text-sm cursor-pointer p-1.5 rounded hover:bg-zinc-100">
            <Checkbox
              checked={v.hcf_differentiators.includes(d)}
              onCheckedChange={() => toggleDiff(d)}
              data-testid={`zoom-diff-${d}`}
            />
            {DIFF_LABELS[d]}
          </label>
        ))}
      </div>
      <div>
        <Label htmlFor="z-hcf-resp">{zt('hcfResponse', 'Risposta del cliente all\'Experience HCF')}</Label>
        <Textarea id="z-hcf-resp" rows={3} value={v.hcf_client_response} onChange={(e) => set('hcf_client_response', e.target.value)} />
      </div>
    </div>
  );
}

function Section7({ v, set }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label>{zt('pathProposed', 'Percorso proposto')}</Label>
          <Select value={v.path_chosen} onValueChange={(x) => set('path_chosen', x)}>
            <SelectTrigger data-testid="zoom-path"><SelectValue placeholder={i18n.t('stageTransition.selectPh', 'Seleziona…')} /></SelectTrigger>
            <SelectContent>{PATH_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Resort</Label>
          <Select value={v.resort_chosen} onValueChange={(x) => set('resort_chosen', x)}>
            <SelectTrigger data-testid="zoom-resort"><SelectValue placeholder={i18n.t('stageTransition.selectPh', 'Seleziona…')} /></SelectTrigger>
            <SelectContent>{RESORT_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label htmlFor="z-units">{zt('unitsProposed', 'Quote/unità proposte')}</Label>
          <Input id="z-units" type="number" min={0} value={v.units_count} onChange={(e) => set('units_count', e.target.value)} placeholder="Es. 2" />
        </div>
        <div>
          <Label htmlFor="z-amount">{zt('amountProposed', 'Importo proposto (EUR)')}</Label>
          <Input id="z-amount" type="number" min={0} value={v.amount_proposed} onChange={(e) => set('amount_proposed', e.target.value)} placeholder="Es. 75000" data-testid="zoom-amount-proposed" />
        </div>
      </div>
      <div>
        <Label>{zt('concreteInterest', 'Il cliente ha mostrato interesse concreto?')}</Label>
        <YesNo value={v.concrete_interest} onChange={(x) => set('concrete_interest', x)} testid="zoom-interest" />
      </div>
    </div>
  );
}

function Section8({ v, set }) {
  return (
    <div className="space-y-3">
      <div>
        <Label>{zt('outcome', 'Esito della Zoom')} *</Label>
        <RadioGroup value={v.outcome} onValueChange={(x) => set('outcome', x)} className="space-y-1.5 mt-2" data-testid="zoom-outcome">
          {OUTCOME_OPTIONS.map((o) => (
            <label key={o.value} className="flex items-start gap-3 cursor-pointer p-2 rounded-md hover:bg-zinc-100 border border-zinc-200/40">
              <RadioGroupItem value={o.value} id={`out-${o.value}`} className="mt-0.5" />
              <div>
                <div className="text-sm font-medium">{o.label}</div>
                <div className="text-xs text-muted-foreground">{o.desc}</div>
              </div>
            </label>
          ))}
        </RadioGroup>
      </div>
      {v.outcome === 'procede' && (
        <div>
          <Label htmlFor="z-conf-amount">{zt('amountConfirmed', 'Importo confermato/pagato (EUR)')}</Label>
          <Input id="z-conf-amount" type="number" min={0} value={v.amount_confirmed} onChange={(e) => set('amount_confirmed', e.target.value)} placeholder={zt('amountConfirmedPh', 'Es. 5000 caparra')} data-testid="zoom-amount-confirmed" />
        </div>
      )}
      {(v.outcome === 'interessato' || v.outcome === 'riflessione') && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label htmlFor="z-next-date">{zt('nextStepDate', 'Prossimo step · data')}</Label>
            <Input id="z-next-date" type="date" value={v.next_step_date} onChange={(e) => set('next_step_date', e.target.value)} />
          </div>
          <div>
            <Label htmlFor="z-next-desc">{zt('nextStepDesc', 'Descrizione step')}</Label>
            <Input id="z-next-desc" value={v.next_step_desc} onChange={(e) => set('next_step_desc', e.target.value)} placeholder={zt('nextStepPh', 'Es. invio documenti + ricall venerdì')} />
          </div>
        </div>
      )}
      <div className="pt-2 border-t border-zinc-200/60">
        <Label className="flex items-center justify-between">
          <span>{zt('finalTemp', 'Temperatura finale del lead (1-10)')}</span>
          <Badge variant="outline">{v.lead_temperature_final}/10</Badge>
        </Label>
        <Slider value={[v.lead_temperature_final]} onValueChange={(x) => set('lead_temperature_final', x[0])} min={1} max={10} step={1} className="mt-2" />
      </div>
      <div>
        <Label htmlFor="z-final-notes">{zt('finalNotes', 'Note finali (opzionale)')}</Label>
        <Textarea id="z-final-notes" rows={3} value={v.notes_final} onChange={(e) => set('notes_final', e.target.value)} />
      </div>
    </div>
  );
}
